import React, { Suspense, useEffect, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ROUTES, updateDocumentTitle } from './config.js';
import { RouteLoadingFallback } from '../components/RouteLoadingFallback.js';
import { KEMYCARD_LAUNCH } from '@pay888/shared';
import { UnavailableProviderFeature } from '../components/ProviderAvailability.js';

// Layouts (Static)
import { PublicLayout } from '../layouts/PublicLayout.js';
import { AuthLayout } from '../layouts/AuthLayout.js';
import { AppLayout } from '../layouts/AppLayout.js';
import { AdminLayout } from '../layouts/AdminLayout.js';

// Initial Critical Routes (Static for instant load and reliability)
import { LandingPage } from '../pages/LandingPage.js';
import { LoginPage } from '../pages/LoginPage.js';
import { RegisterPage } from '../pages/RegisterPage.js';
import { ForgotPasswordPage } from '../pages/ForgotPasswordPage.js';
import { DashboardPage } from '../pages/DashboardPage.js';
const PreviewPage = lazy(() =>
  import('../pages/PreviewPage.js').then((m) => ({ default: m.PreviewPage }))
);
const ServicesPage = lazy(() =>
  import('../pages/ServicesPage.js').then((m) => ({ default: m.ServicesPage }))
);
const ReferralsPage = lazy(() =>
  import('../pages/ReferralsPage.js').then((m) => ({ default: m.ReferralsPage }))
);

// Lazy-loaded Public & Auth Routes
const VerifyEmailPage = lazy(() =>
  import('../pages/VerifyEmailPage.js').then((m) => ({ default: m.VerifyEmailPage }))
);
const PublicPaymentPage = lazy(() =>
  import('../pages/PublicPaymentPage.js').then((m) => ({ default: m.PublicPaymentPage }))
);

// Lazy-loaded Protected App Routes
const ReceivePage = lazy(() =>
  import('../pages/ReceivePage.js').then((m) => ({ default: m.ReceivePage }))
);
const ReceiveCreatePage = lazy(() =>
  import('../pages/ReceiveCreatePage.js').then((m) => ({ default: m.ReceiveCreatePage }))
);
const ReceivePaymentsPage = lazy(() =>
  import('../pages/ReceivePaymentsPage.js').then((m) => ({ default: m.ReceivePaymentsPage }))
);
const ReceiveLinksPage = lazy(() =>
  import('../pages/ReceiveLinksPage.js').then((m) => ({ default: m.ReceiveLinksPage }))
);
const ReceiveEditPage = lazy(() =>
  import('../pages/ReceiveEditPage.js').then((m) => ({ default: m.ReceiveEditPage }))
);
const CardsPage = lazy(() =>
  import('../pages/CardsPage.js').then((m) => ({ default: m.CardsPage }))
);
const CardsCreatePage = lazy(() =>
  import('../pages/CardsCreatePage.js').then((m) => ({ default: m.CardsCreatePage }))
);
const CardsTransactionsPage = lazy(() =>
  import('../pages/CardsTransactionsPage.js').then((m) => ({ default: m.CardsTransactionsPage }))
);
const ActivityPage = lazy(() =>
  import('../pages/ActivityPage.js').then((m) => ({ default: m.ActivityPage }))
);
const SettingsPage = lazy(() =>
  import('../pages/SettingsPage.js').then((m) => ({ default: m.SettingsPage }))
);
const SupportPage = lazy(() =>
  import('../pages/SupportPage.js').then((m) => ({ default: m.SupportPage }))
);

// Lazy-loaded Admin Routes
const AdminOverviewPage = lazy(() =>
  import('../pages/AdminOverviewPage.js').then((m) => ({ default: m.AdminOverviewPage }))
);
const AdminUsersPage = lazy(() =>
  import('../pages/AdminUsersPage.js').then((m) => ({ default: m.AdminUsersPage }))
);
const AdminPaymentsPage = lazy(() =>
  import('../pages/AdminPaymentsPage.js').then((m) => ({ default: m.AdminPaymentsPage }))
);
const AdminCardsPage = lazy(() =>
  import('../pages/AdminCardsPage.js').then((m) => ({ default: m.AdminCardsPage }))
);
const AdminRevenuePage = lazy(() =>
  import('../pages/AdminRevenuePage.js').then((m) => ({ default: m.AdminRevenuePage }))
);
const AdminProvidersPage = lazy(() =>
  import('../pages/AdminProvidersPage.js').then((m) => ({ default: m.AdminProvidersPage }))
);
const AdminSettingsPage = lazy(() =>
  import('../pages/AdminSettingsPage.js').then((m) => ({ default: m.AdminSettingsPage }))
);
const AdminAuditPage = lazy(() =>
  import('../pages/AdminAuditPage.js').then((m) => ({ default: m.AdminAuditPage }))
);

// Lazy-loaded Fallback
const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage.js').then((m) => ({ default: m.NotFoundPage }))
);

import { RequireAuth } from './guards/RequireAuth.js';
import { RequireAdmin } from './guards/RequireAdmin.js';

export const AppRoutes: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const matchedRoute = Object.values(ROUTES).find((r) => {
      if (r.path.includes(':slug')) {
        return location.pathname.startsWith('/pay/');
      }
      return r.path === location.pathname;
    });

    if (location.pathname === '/preview' || location.pathname.startsWith('/preview/')) {
      updateDocumentTitle('App Preview');
    } else if (matchedRoute) {
      updateDocumentTitle(matchedRoute.title);
    } else {
      updateDocumentTitle('Page Not Found');
    }
  }, [location.pathname]);

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route element={<AppLayout preview />}>
          <Route path="/preview" element={<PreviewPage />} />
          <Route path="/preview/services" element={<ServicesPage />} />
          <Route path="/preview/referrals" element={<ReferralsPage preview />} />
          <Route path="/preview/receive" element={<UnavailableProviderFeature />} />
          <Route path="/preview/:section" element={<PreviewPage />} />
        </Route>
        {/* Public Routes Group */}
        <Route element={<PublicLayout />}>
          <Route path={ROUTES.HOME.path} element={<LandingPage />} />
          <Route
            path={ROUTES.PUBLIC_PAY.path}
            element={
              KEMYCARD_LAUNCH.acquiringEnabled ? (
                <PublicPaymentPage />
              ) : (
                <UnavailableProviderFeature />
              )
            }
          />
        </Route>

        {/* Auth Routes Group */}
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.LOGIN.path} element={<LoginPage />} />
          <Route path={ROUTES.REGISTER.path} element={<RegisterPage />} />
          <Route path={ROUTES.FORGOT_PASSWORD.path} element={<ForgotPasswordPage />} />
          <Route path={ROUTES.VERIFY_EMAIL.path} element={<VerifyEmailPage />} />
        </Route>

        <Route element={<RequireAuth />}>
          <Route element={<AppLayout />}>
            <Route path={ROUTES.DASHBOARD.path} element={<DashboardPage />} />
            <Route
              path={ROUTES.RECEIVE.path}
              element={
                KEMYCARD_LAUNCH.acquiringEnabled ? <ReceivePage /> : <UnavailableProviderFeature />
              }
            />
            <Route
              path={ROUTES.RECEIVE_CREATE.path}
              element={
                KEMYCARD_LAUNCH.acquiringEnabled ? (
                  <ReceiveCreatePage />
                ) : (
                  <UnavailableProviderFeature />
                )
              }
            />
            <Route
              path={ROUTES.RECEIVE_PAYMENTS.path}
              element={
                KEMYCARD_LAUNCH.acquiringEnabled ? (
                  <ReceivePaymentsPage />
                ) : (
                  <UnavailableProviderFeature />
                )
              }
            />
            <Route
              path={ROUTES.RECEIVE_LINKS.path}
              element={
                KEMYCARD_LAUNCH.acquiringEnabled ? (
                  <ReceiveLinksPage />
                ) : (
                  <UnavailableProviderFeature />
                )
              }
            />
            <Route
              path="/receive/links/:slug/edit"
              element={
                KEMYCARD_LAUNCH.acquiringEnabled ? (
                  <ReceiveEditPage />
                ) : (
                  <UnavailableProviderFeature />
                )
              }
            />
            <Route path={ROUTES.CARDS.path} element={<CardsPage />} />
            <Route path={ROUTES.SERVICES.path} element={<ServicesPage />} />
            <Route path={ROUTES.REFERRALS.path} element={<ReferralsPage />} />
            <Route path={ROUTES.CARDS_CREATE.path} element={<CardsCreatePage />} />
            <Route path={ROUTES.CARDS_TRANSACTIONS.path} element={<CardsTransactionsPage />} />
            <Route path={ROUTES.ACTIVITY.path} element={<ActivityPage />} />
            <Route path={ROUTES.SETTINGS.path} element={<SettingsPage />} />
            <Route path={ROUTES.SUPPORT.path} element={<SupportPage />} />
          </Route>
        </Route>

        <Route element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route path={ROUTES.ADMIN.path} element={<AdminOverviewPage />} />
            <Route path={ROUTES.ADMIN_USERS.path} element={<AdminUsersPage />} />
            <Route path={ROUTES.ADMIN_PAYMENTS.path} element={<AdminPaymentsPage />} />
            <Route path={ROUTES.ADMIN_CARDS.path} element={<AdminCardsPage />} />
            <Route path={ROUTES.ADMIN_REVENUE.path} element={<AdminRevenuePage />} />
            <Route path={ROUTES.ADMIN_PROVIDERS.path} element={<AdminProvidersPage />} />
            <Route path={ROUTES.ADMIN_SETTINGS.path} element={<AdminSettingsPage />} />
            <Route path={ROUTES.ADMIN_AUDIT.path} element={<AdminAuditPage />} />
          </Route>
        </Route>

        {/* 404 Fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};
